import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { InputDialog } from "../customComponents/InputDialog";
import { TextareaFormField } from "../customComponents/TextareaFromField";
import BillsService, {
  INotices,
} from "@/api-client/api/services/billsService/billsService";
import TrashBind from "@/assets/icon/svgs/TrashBind.svg?react";
import { PopUpConfirmDelete } from "./ConfirmDelete";
import CheckBox from "../checkbox/CheckBox";
import { Loader } from "@/assets/icon/loader";
type FormData = {
  title: string;
  description: string;
};

export function PopUpCreateNews({
  trigger,
  item,
  building_id,
  fetchNotices,
}: {
  trigger: React.ReactNode;
  item?: INotices;
  building_id: number;
  fetchNotices: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [doesNewsEmergensi, setDoesNewsEmergensi] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<FormData>({
    defaultValues: {
      title: item?.title || "",
      description: item?.description || "",
    },
  });

  const {
    watch,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
  } = methods;

  const title = watch("title");

  const handleDialogClose = (isOpen: boolean) => {
    setIsOpen(isOpen);
    if (!isOpen) {
      methods.reset();
    }
  };

  const validateForm = (data: FormData): boolean => {
    let hasErrors = false;

    if (!data.title.trim()) {
      setError("title", { message: t("popUpCreateNews.required_title") });
      hasErrors = true;
    }

    if (!data.description.trim()) {
      setError("description", {
        message: t("popUpCreateNews.required_description"),
      });
      hasErrors = true;
    }

    return hasErrors;
  };

  const createNews = async (data: FormData) => {
    setIsLoading(true);
    try {
      const response = await BillsService.postNotice({
        ...data,
        building_id: building_id,
        is_emergency: doesNewsEmergensi,
      });
      setIsLoading(false);
      fetchNotices();
      handleDialogClose(false);
      return response;
    } catch (error) {
      console.error("Error while creating news:", error);
    }
  };

  const updateNews = async (data: FormData) => {
    setIsLoading(true);
    if (!item?.id) return;
    try {
      const response = await BillsService.patchNotice(item.id, {
        ...data,
        building_id: building_id,
      });
      handleDialogClose(false);
      fetchNotices();
      setIsLoading(false);
      return response;
    } catch (error) {
      console.error("Error while updating news:", error);
    }
  };

  const deleteNews = async () => {
    if (!item?.id) return;
    try {
      const response = await BillsService.deleteNotice(item.id);
      setIsOpen(false);
      fetchNotices();
      return response;
    } catch (error) {
      console.error("Error while deleting news:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose} modal={false}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      {isOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[4px] z-40 pointer-events-none"></div>
      )}
      <DialogContent
        className="max-h-full overflow-auto gap-4 min-w-[680px] pb-[40px]"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="mb-[16px]">
          <DialogTitle className="text-[24px] font-medium flex items-center">
            {t("popUpCreateNews.create_news")}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          {isLoading ? (
            <div className="flex justify-center items-center gap-2">
              <span className="text-[20px] font-medium">
                {t("popUpCreateNews.news_will_be_published_soon")}
              </span>
              <Loader />
            </div>
          ) : (
            <form
              onSubmit={handleSubmit((data) => {
                const hasErrors = validateForm(data);
                if (!hasErrors) {
                  if (item) {
                    updateNews(data);
                  } else {
                    createNews(data);
                  }
                }
              })}
              className="flex flex-col gap-6"
            >
              <div className="flex flex-col gap-6">
                <span className="text-[20px] font-medium">
                  {t("popUpCreateNews.content")}
                </span>
                <div className="flex flex-col gap-2 items-end">
                  <div className="w-full">
                    <InputDialog
                      id="popUpCreateNews.NewsHeadline"
                      validationError={errors.title?.message || ""}
                      placeholder={t("popUpCreateNews.news_headline")}
                      isBiggerInput={true}
                      inputValue={title}
                      disablePlaceholderAnimation={false}
                      setValidationError={() => {}}
                      setInputValue={(value) => {
                        const limitedValue = value.slice(0, 50);
                        setValue("title", limitedValue);
                      }}
                    />
                  </div>
                  <span className="text-[#00000080] text-[15px] font-medium">
                    {t("popUpCreateNews.symbols")}: 50
                  </span>
                </div>

                <div className="flex flex-col gap-2 items-end">
                  <div className="w-full">
                    <TextareaFormField
                      control={methods.control}
                      name="description"
                      placeholder={t("popUpCreateNews.news_text")}
                      maxLength={200}
                    />
                  </div>
                  <span className="text-[#00000080] text-[15px] font-medium">
                    {t("popUpCreateNews.symbols")}: 200
                  </span>
                </div>
              </div>
              {item && (
                <div className="flex justify-end">
                  <PopUpConfirmDelete
                    onConfirm={deleteNews}
                    trigger={
                      <div className="flex flex-row gap-2 items-center text-[20px]text-[#00000080] cursor-pointer">
                        <TrashBind width={24} height={24} />{" "}
                        <span>
                          {t("popUpConfirmDelete.delete_publication")}
                        </span>
                      </div>
                    }
                  />
                </div>
              )}

              <div className="w-full h-[1px] bg-[#0000001A]" />

              <div className="flex justify-between items-center">
                <div className="flex flex-row gap-4">
                  {!item && (
                    <CheckBox
                      checked={doesNewsEmergensi}
                      onChange={setDoesNewsEmergensi}
                    />
                  )}
                  <p className="font-medium text-[15px] leading-[120%]">
                    {t("popUpCreateNews.checkBox_description")}
                  </p>
                </div>
                <Button
                  type="submit"
                  variant="system"
                  size="system"
                  disabled={false}
                  loading={false}
                  className="w-fit px-5"
                >
                  {item
                    ? t("popUpCreateNews.save_and_republish")
                    : t("popUpCreateNews.save_and_publish")}
                </Button>
              </div>
            </form>
          )}
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
